import { UNSAFE_DataRouterStateContext } from 'react-router';
import { useContext } from 'react';

type ScreenNameResolver = (
    params: Record<string, string | undefined>
) => string;

export interface PageViewHandle {
    screen_name: string | ScreenNameResolver;
}

function isPageViewHandle(handle: unknown): handle is PageViewHandle {
    if (typeof handle !== 'object' || handle === null) return false;
    const sn = (handle as { screen_name: unknown }).screen_name;
    return typeof sn === 'string' || typeof sn === 'function';
}

/**
 * Resolves the active route's `screen_name` from the React Router `handle`
 * prop co-located on each `<Route>`. Two handle forms are supported:
 *
 *   // Constant
 *   handle={{ screen_name: Names.PROPERTY }}
 *
 *   // Function — dual-purpose routes whose name depends on url params
 *   handle={{ screen_name: (p) => p.setting ? Names.REAUTH_PASSWORD
 *                                            : Names.LOGIN_PASSWORD }}
 *
 * Returns the deepest matched route's screen name, or `undefined` when no
 * matched route declares one.
 *
 * This is the single source of truth for "what screen is the user on":
 * `usePageView` fires `page_view` from it, and interaction events
 * (`cta_click`, `link_click`, `content_view`, …) tag themselves with it —
 * the web equivalent of mobile's `BaseViewModel.analyticsScreenName`.
 *
 * Reads the data-router state directly instead of `useMatches()` so it degrades
 * to `undefined` when rendered outside a data router (e.g. a component mounted
 * in isolation in a unit test) rather than throwing — analytics never gates
 * rendering. In the app the tree is always inside a `RouterProvider`.
 */
export function useScreenName(): string | undefined {
    const state = useContext(UNSAFE_DataRouterStateContext);
    if (!state || !Array.isArray(state.matches)) return undefined;
    return [...state.matches]
        .reverse()
        .map(m => {
            const handle = m.route.handle;
            if (!isPageViewHandle(handle)) return null;
            const sn = handle.screen_name;
            return typeof sn === 'function'
                ? sn(m.params as Record<string, string | undefined>)
                : sn;
        })
        .find((s): s is string => typeof s === 'string' && s.length > 0);
}
